import React, { Suspense, useEffect } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// Context
import { useMaterialUIController } from "context";

// Clerk
import { ClerkProvider } from "@clerk/clerk-react";
import { deDE } from "@clerk/localizations";

// Auth guard & layout
import RequireAuth from "components/RequireAuth";
import AuthenticatedLayout from "components/AuthenticatedLayout";

// Eagerly loaded (small, needed immediately)
import LoadingStart from "modules/loadingStart";
import SignUpCoverPage from "layouts/authentication/sign-up/cover";
import SignInPage from "layouts/authentication/sign-in/basic";

// Sidenav routes (needed for route generation)
import sidenavRoutes from "sidenavRoutes";

// Lazy-loaded modules
const Dashboard = React.lazy(() => import("modules/dashboard"));
const Profile = React.lazy(() => import("modules/profile"));
const ProjectDetailsPage = React.lazy(() => import("modules/projectDetails"));
const MaterialDetailsPage = React.lazy(() => import("modules/materialDetails"));
const CustomerDetailsPage = React.lazy(() => import("modules/customerDetails"));
const EmployeeDetailsPage = React.lazy(() => import("modules/employeeDetails"));
const WorkreportDetailsPage = React.lazy(() => import("modules/workreportDetails"));

// Lazy-loaded pages
const SingleArticle = React.lazy(() => import("pages/Blogs/SingleArticle"));
const Impressum = React.lazy(() => import("pages/Support/Impressum"));
const PrivacyPage = React.lazy(() => import("layouts/pages/support/privacy"));

// Loading fallback for public pages only
function SuspenseFallback() {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <div style={{ fontSize: 18, color: "#555", fontFamily: "sans-serif" }}>
        Lade Seite...
      </div>
    </div>
  );
}

if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

function ClerkProviderWithRoutes() {
  const navigate = useNavigate();

  // Generate child routes from sidenavRoutes (no RequireAuth wrapper — parent layout handles it)
  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }
      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.name}
          />
        );
      }
      return null;
    });

  return (
    <ClerkProvider publishableKey={clerkPubKey} localization={deDE} navigate={(to) => navigate(to)}>
      <Routes key="routes">
        {/* Public routes */}
        <Route key="LoadingStart" path="/" element={<LoadingStart />} />
        <Route key="register" path="/register" element={<SignUpCoverPage />} />
        <Route key="login" path="/login" element={<SignInPage />} />
        <Route
          key="impressum"
          path="/impressum"
          element={<Suspense fallback={<SuspenseFallback />}><Impressum /></Suspense>}
        />
        <Route
          key="article"
          path="/article/:id"
          element={<Suspense fallback={<SuspenseFallback />}><SingleArticle /></Suspense>}
        />
        <Route
          key="privacy"
          path="/privacy"
          element={<Suspense fallback={<SuspenseFallback />}><PrivacyPage /></Suspense>}
        />

        {/* Authenticated layout route — sidebar/navbar persist, only Outlet re-renders */}
        <Route element={<RequireAuth><AuthenticatedLayout /></RequireAuth>}>
          <Route key="home" path="/dashboard" element={<Dashboard />} />
          <Route key="profile" path="/profile" element={<Profile />} />
          <Route key="projectDetails" path="/projects/details" element={<ProjectDetailsPage />} />
          <Route key="materialDetails" path="/materials/details" element={<MaterialDetailsPage />} />
          <Route key="customerDetails" path="/customers/details" element={<CustomerDetailsPage />} />
          <Route key="employeeDetails" path="/employees/details" element={<EmployeeDetailsPage />} />
          <Route key="workreportDetails" path="/workreports/details" element={<WorkreportDetailsPage />} />
          {getRoutes(sidenavRoutes)}
          <Route key="redirectAll" path="*" element={<Navigate to="/dashboard" />} />
        </Route>
      </Routes>
    </ClerkProvider>
  );
}

export default function App() {
  const [controller] = useMaterialUIController();
  const { pathname } = useLocation();
  const { direction, darkMode } = controller;

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <ClerkProviderWithRoutes />
    </ThemeProvider>
  );
}
